import React, { useEffect } from 'react';
import { useStore } from '../../../../stores/root';
import PermissionsService from '../../../../services/permissions';
import { DatePicker, Dropdown, Stack } from '@fluentui/react';
import NoPermission from '../../../uiframeworks/noPermission';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import GeneralService from '../../../../services/general';
import IncomeInvoicesSummary from './invoices';

type IncomesSummaryProps = {};

export type TSearchBy = 'invoices' | 'payments';
export type TBasedOn = 'createdDate' | 'dueDate';

const IncomesSummary: React.FC<IncomesSummaryProps> = (props: IncomesSummaryProps) => {
    const { banner, user } = useStore();
    const [searchBy, setSearchBy] = React.useState<TSearchBy>("invoices");
    const [basedOn, setBasedOn] = React.useState<TBasedOn>("createdDate");
    const [dateRange, setDateRange] = React.useState<string>("thismonth");
    const [selectedDate, setSelectedDate] = React.useState<{ startDate: string, endDate: string }>({
        startDate: moment().startOf('month').toISOString(),
        endDate: moment().endOf('month').toISOString()
    });

    const hasPermission = PermissionsService.hasPermission(['incomes.invoices.read.all', 'incomes.invoices.read.me', 'orders.read.all', 'orders.read.me'], user.permissions);

    return <>
        {!hasPermission ? <NoPermission /> : null}
        {hasPermission ? <Stack tokens={{ childrenGap: 20 }} styles={{ root: { width: '100%' } }}>
            <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Dropdown label={'Search by'}
                    options={[
                        { key: 'invoices', text: 'Invoices' },
                        { key: 'payments', text: 'Payments', disabled: true }
                    ]}
                    styles={{ root: { width: 180 } }}
                    selectedKey={searchBy}
                    onChange={(ev, opt) => {
                        setSearchBy(opt?.key as TSearchBy || 'invoices');
                    }} />
                <Dropdown label={'Based on'}
                    options={[
                        { key: 'createdDate', text: 'Created date' },
                        { key: 'dueDate', text: 'Due date' }
                    ]}
                    styles={{ root: { width: 180 } }}
                    selectedKey={basedOn}
                    onChange={(ev, opt) => {
                        setBasedOn(opt?.key as TBasedOn || 'createdDate');
                    }} />
                <Dropdown label={'Date range'}
                    options={[
                        { key: 'thisweek', text: 'This week' },
                        { key: 'lastweek', text: 'Last week' },
                        { key: 'thismonth', text: 'This month' },
                        { key: 'lastmonth', text: 'Last month' },
                        { key: 'last3month', text: 'Last 3 months' },
                        { key: 'last6month', text: 'Last 6 months' },
                        { key: 'thisyear', text: 'This year' },
                        { key: 'lastyear', text: 'Last year' },
                        { key: 'custom', text: 'Custom' }
                    ]}
                    styles={{ root: { width: 180 } }}
                    selectedKey={dateRange}
                    onChange={(ev, opt) => {
                        let _startDate = selectedDate.startDate;
                        let _endDate = selectedDate.endDate;
                        let _dateRange = opt?.key as string || dateRange;

                        if (_dateRange === 'thismonth') {
                            _startDate = moment().startOf('month').toISOString();
                            _endDate = moment().endOf('month').toISOString();
                        } else if (_dateRange === 'lastmonth') {
                            _startDate = moment().startOf('month').add(-1, 'month').toISOString();
                            _endDate = moment().endOf('month').add(-1, 'month').toISOString();
                        } else if (_dateRange === 'last3month') {
                            _startDate = moment().startOf('month').add(-2, 'month').toISOString();
                            _endDate = moment().endOf('month').toISOString();
                        } else if (_dateRange === 'last6month') {
                            _startDate = moment().startOf('month').add(-5, 'month').toISOString();
                            _endDate = moment().endOf('month').toISOString();
                        } else if (_dateRange === 'thisweek') {
                            _startDate = moment().startOf('week').toISOString();
                            _endDate = moment().endOf('week').toISOString();
                        } else if (_dateRange === 'lastweek') {
                            _startDate = moment().startOf('week').add(-1, 'week').toISOString();
                            _endDate = moment().endOf('week').add(-1, 'week').toISOString();
                        } else if (_dateRange === 'thisyear') {
                            _startDate = moment().startOf('year').toISOString();
                            _endDate = moment().endOf('year').toISOString();
                        } else if (_dateRange === 'lastyear') {
                            _startDate = moment().startOf('year').add(-1, 'year').toISOString();
                            _endDate = moment().endOf('year').add(-1, 'year').toISOString();
                        }

                        setSelectedDate({ startDate: _startDate, endDate: _endDate });
                        setDateRange(_dateRange);
                    }} />
                {dateRange === 'custom' ? <Stack verticalAlign='center' horizontal tokens={{ childrenGap: 20 }} styles={{ root: { paddingTop: 26 } }}>
                    <DatePicker value={moment(selectedDate.startDate).toDate()}
                        styles={{ root: { width: 140 } }}
                        formatDate={GeneralService.formatDate}
                        onSelectDate={(date) => {
                            const _startDate = date ? moment(date).startOf('day').toISOString() : selectedDate.startDate;
                            setSelectedDate({ startDate: _startDate, endDate: selectedDate.endDate });
                        }} />
                    <Stack><FontAwesomeIcon icon={faArrowRight} /></Stack>
                    <DatePicker value={moment(selectedDate.endDate).toDate()}
                        styles={{ root: { width: 140 } }}
                        formatDate={GeneralService.formatDate}
                        onSelectDate={(date) => {
                            const _endDate = date ? moment(date).endOf('day').toISOString() : selectedDate.endDate;
                            setSelectedDate({ startDate: selectedDate.startDate, endDate: _endDate });
                        }} />
                </Stack> : null}
            </Stack>
            <Stack className={'divider'}></Stack>
            {searchBy === 'invoices' ? <IncomeInvoicesSummary basedOn={basedOn} {...selectedDate} /> : null}
        </Stack> : null}
    </>;
};

export default IncomesSummary;
